import React, { useState, useEffect, useCallback } from 'react';
import api from './api';
import Spinner from './Spinner';
import CustomAlert from './CustomAlert';

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    const colors = {
        bg: isDark ? '#121212' : '#f4f7f6',
        cardBg: isDark ? '#1e1e1e' : '#fff',
        text: isDark ? '#e0e0e0' : '#333',
        subtleText: isDark ? '#a0a0a0' : '#666',
        border: isDark ? '#444' : '#ddd',
        shadow: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        link: isDark ? '#6cb2f7' : '#007bff',
    };

    return {
        container: { 
            backgroundColor: colors.cardBg, 
            borderRadius: '8px', 
            overflow: 'hidden', 
            boxShadow: `0 2px 5px ${colors.shadow}`,
            marginTop: '20px'
        },
        header: {
            backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa',
            padding: '15px 20px',
            borderBottom: `2px solid ${colors.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
        },
        title: { 
            fontSize: '18px', 
            fontWeight: 'bold', 
            color: colors.text, 
            margin: 0 
        },
        controlsContainer: { 
            display: 'flex', 
            gap: '15px', 
            marginBottom: '20px', 
            flexWrap: 'wrap', 
            alignItems: 'center',
            padding: '15px 20px',
            borderBottom: `1px solid ${colors.border}`
        },
        searchInput: { 
            padding: '8px 12px', 
            borderRadius: '4px', 
            border: `1px solid ${colors.border}`, 
            backgroundColor: isDark ? '#333' : '#fff', 
            color: colors.text, 
            minWidth: '200px' 
        },
        filterSelect: { 
            padding: '8px 12px', 
            borderRadius: '4px', 
            border: `1px solid ${colors.border}`, 
            backgroundColor: isDark ? '#333' : '#fff', 
            color: colors.text 
        },
        table: { 
            width: '100%', 
            borderCollapse: 'collapse' 
        },
        tableHeader: { 
            backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa', 
            padding: '15px 10px', 
            borderBottom: `2px solid ${colors.border}` 
        },
        tableHeaderCell: { 
            padding: '12px 8px', 
            textAlign: 'left', 
            fontWeight: 'bold', 
            color: colors.text, 
            cursor: 'pointer', 
            userSelect: 'none', 
            position: 'relative',
            fontSize: '12px',
            whiteSpace: 'nowrap'
        },
        sortableHeader: { 
            display: 'flex', 
            alignItems: 'center', 
            gap: '5px' 
        },
        sortIcon: { 
            fontSize: '10px', 
            opacity: 0.7 
        },
        tableRow: { 
            borderBottom: `1px solid ${colors.border}`, 
            '&:hover': { backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa' } 
        },
        tableCell: { 
            padding: '8px', 
            color: colors.text, 
            verticalAlign: 'top',
            fontSize: '11px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '120px'
        },
        siteBadge: {
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '10px',
            fontWeight: 'bold',
            backgroundColor: colors.link,
            color: 'white'
        },
        noData: {
            textAlign: 'center',
            padding: '40px',
            color: colors.subtleText,
            fontStyle: 'italic'
        },
        uploadButton: {
            padding: '8px 16px',
            backgroundColor: '#ffc107',
            color: 'black',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px'
        },
        paginationContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '15px 20px',
            borderTop: `1px solid ${colors.border}`,
            backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa'
        },
        paginationInfo: {
            color: colors.subtleText,
            fontSize: '14px'
        },
        paginationControls: {
            display: 'flex',
            gap: '10px',
            alignItems: 'center'
        },
        paginationButton: {
            padding: '6px 12px',
            backgroundColor: colors.cardBg,
            color: colors.text,
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px',
            minWidth: '40px'
        },
        paginationButtonDisabled: {
            padding: '6px 12px',
            backgroundColor: isDark ? '#333' : '#f5f5f5',
            color: colors.subtleText,
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            cursor: 'not-allowed',
            fontSize: '12px',
            minWidth: '40px'
        },
        pageNumbers: {
            display: 'flex',
            gap: '5px',
            alignItems: 'center'
        },
        pageNumber: {
            padding: '6px 10px',
            backgroundColor: colors.cardBg,
            color: colors.text,
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px',
            minWidth: '30px',
            textAlign: 'center'
        },
        currentPage: {
            padding: '6px 10px',
            backgroundColor: colors.link,
            color: 'white',
            border: `1px solid ${colors.link}`,
            borderRadius: '4px',
            fontSize: '12px',
            minWidth: '30px',
            textAlign: 'center'
        }
    };
};

function QTDACTimeTable({ theme, sites, onUpload }) {
    const styles = getStyles(theme);
    const [qtdACTimeData, setQtdACTimeData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [siteFilter, setSiteFilter] = useState('');
    const [sortField, setSortField] = useState('');
    const [sortDirection, setSortDirection] = useState('asc');
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [pageSize] = useState(100);

    // Data is now filtered and sorted server-side
    const displayData = qtdACTimeData;

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const fetchQtdACTimeData = useCallback((page = 1) => {
        setLoading(true);
        const params = new URLSearchParams();
        params.append('page', page);
        params.append('page_size', pageSize);
        
        // Add search and filter parameters if they exist
        if (searchTerm) params.append('search', searchTerm);
        if (siteFilter) params.append('site_name', siteFilter);
        
        api.get(`/qtd-ac-time/?${params.toString()}`)
            .then(res => {
                const data = Array.isArray(res.data) ? res.data : (res.data.results || []);
                setQtdACTimeData(data);
                
                // Handle pagination metadata
                if (res.data.count !== undefined) {
                    setTotalCount(res.data.count);
                    setTotalPages(Math.ceil(res.data.count / pageSize));
                } else {
                    setTotalCount(data.length);
                    setTotalPages(1);
                }
                setCurrentPage(page);
            })
            .catch(err => {
                console.error('Error fetching QTD AC time data:', err);
                setAlert({ show: true, message: 'Failed to fetch QTD AC Time data.', type: 'error' });
            })
            .finally(() => setLoading(false));
    }, [pageSize, searchTerm, siteFilter]);

    useEffect(() => {
        fetchQtdACTimeData(1);
    }, [fetchQtdACTimeData]);

    // Pagination functions
    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            fetchQtdACTimeData(page);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            handlePageChange(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            handlePageChange(currentPage + 1);
        }
    };

    // Generate page numbers to display
    const getPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
        
        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }
        
        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }
        return pages;
    };

    const handleUploadClick = () => {
        onUpload('Raw Data - QTD AC Time', 'Quarterly aircraft time data');
    };

    return (
        <div style={styles.container}>
            <CustomAlert {...alert} onClose={() => setAlert({ show: false })} theme={theme} />
            
            <div style={styles.header}>
                <h2 style={styles.title}>QTD AC Time Data</h2>
                <button onClick={handleUploadClick} style={styles.uploadButton}>
                    Upload New Data
                </button>
            </div>

            <div style={styles.controlsContainer}>
                <input
                    type="text"
                    placeholder="Search by TEC, Serno, Modex, or other fields..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={styles.searchInput}
                />
                <select
                    value={siteFilter}
                    onChange={(e) => setSiteFilter(e.target.value)}
                    style={styles.filterSelect}
                >
                    <option value="">All Sites</option>
                    {Array.isArray(sites) && sites.map(site => (
                        <option key={site.id} value={site.name}>{site.name}</option>
                    ))}
                </select>
            </div>

            {loading ? (
                <div style={{ padding: '40px', textAlign: 'center' }}>
                    <Spinner />
                </div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={styles.table}>
                        <thead>
                            <tr style={styles.tableHeader}>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('site_name')}>
                                        Site
                                        {sortField === 'site_name' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('s_tec')}>
                                        TEC
                                        {sortField === 's_tec' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('s_serno')}>
                                        Serno
                                        {sortField === 's_serno' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('s_modex')}>
                                        Modex
                                        {sortField === 's_modex' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>Maint Unsch</th>
                                <th style={styles.tableHeaderCell}>Look</th>
                                <th style={styles.tableHeaderCell}>Fix</th>
                                <th style={styles.tableHeaderCell}>Accept_Transfer</th>
                                <th style={styles.tableHeaderCell}>Condition</th>
                                <th style={styles.tableHeaderCell}>Special Look</th>
                                <th style={styles.tableHeaderCell}>Special Fix</th>
                                <th style={styles.tableHeaderCell}>TDC</th>
                                <th style={styles.tableHeaderCell}>Man Hours</th>
                                <th style={styles.tableHeaderCell}>ACFT Flight Hrs</th>
                                <th style={styles.tableHeaderCell}>DMMH Flight Hrs</th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayData.length === 0 ? (
                                <tr>
                                    <td colSpan="15" style={styles.noData}>
                                        No QTD AC Time data available. Upload data to get started.
                                    </td>
                                </tr>
                            ) : (
                                displayData.map((row, index) => (
                                    <tr key={`${row.site_name}-${row.s_modex}-${index}`} style={styles.tableRow}>
                                        <td style={styles.tableCell}>
                                            <span style={styles.siteBadge}>{row.site_name}</span>
                                        </td>
                                        <td style={styles.tableCell}>{row.s_tec}</td>
                                        <td style={styles.tableCell}>{row.s_serno}</td>
                                        <td style={styles.tableCell}>{row.s_modex}</td>
                                        <td style={styles.tableCell}>{row.dc_maint_unsch}</td>
                                        <td style={styles.tableCell}>{row.dc_look}</td>
                                        <td style={styles.tableCell}>{row.dc_fix}</td>
                                        <td style={styles.tableCell}>{row.dc_acpt_xfer}</td>
                                        <td style={styles.tableCell}>{row.dc_cond}</td>
                                        <td style={styles.tableCell}>{row.dc_special_look}</td>
                                        <td style={styles.tableCell}>{row.dc_special_fix}</td>
                                        <td style={styles.tableCell}>{row.dc_tdc}</td>
                                        <td style={styles.tableCell}>{row.dc_mhrs}</td>
                                        <td style={styles.tableCell}>{row.dc_acft_flthrs}</td>
                                        <td style={styles.tableCell}>{row.dc_dmmh_flthrs}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}
            
            {/* Pagination Controls */}
            {!loading && totalPages > 1 && (
                <div style={styles.paginationContainer}>
                    <div style={styles.paginationInfo}>
                        Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} records
                    </div>
                    <div style={styles.paginationControls}>
                        <button
                            onClick={handlePreviousPage}
                            disabled={currentPage === 1}
                            style={currentPage === 1 ? styles.paginationButtonDisabled : styles.paginationButton}
                        >
                            Previous
                        </button>
                        
                        <div style={styles.pageNumbers}>
                            {getPageNumbers().map(pageNum => (
                                <button
                                    key={pageNum}
                                    onClick={() => handlePageChange(pageNum)}
                                    style={pageNum === currentPage ? styles.currentPage : styles.pageNumber}
                                >
                                    {pageNum}
                                </button>
                            ))}
                        </div>
                        
                        <button
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages}
                            style={currentPage === totalPages ? styles.paginationButtonDisabled : styles.paginationButton}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default QTDACTimeTable;